import { useEffect, useRef, useState } from 'react';

const DemoVideo = () => {
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#0a1628] py-20 px-6" id="demo-video" ref={ref}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-video-line text-[#f97316] text-sm"></i>
            <span className="text-gray-300 text-xs font-semibold tracking-widest uppercase">Product Demo</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            See PRODIFY in Action —
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#f97316] to-[#3b82f6]">
              Watch the Full Demo
            </span>
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            A quick walkthrough of how PRODIFY transforms your workshop — from booking a job to generating a VAT invoice, all in minutes.
          </p>
        </div>

        {/* Video container */}
        <div
          className={`relative rounded-2xl overflow-hidden border border-white/10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ aspectRatio: '16/9' }}
        >
          {!playing ? (
            /* Thumbnail overlay */
            <div
              className="absolute inset-0 cursor-pointer group"
              onClick={() => setPlaying(true)}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/295c7ce5d341b5ae865815974379c053.jpg"
                alt="PRODIFY Demo Video Thumbnail"
                className="w-full h-full object-cover object-top"
              />
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-[#0a1628]/60 group-hover:bg-[#0a1628]/50 transition-all duration-300"></div>
              {/* Gradient edges */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 via-transparent to-[#0a1628]/20"></div>

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#f97316] rounded-full group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-play-fill text-white text-3xl ml-1"></i>
                </div>
              </div>

              {/* Bottom label */}
              <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                <div className="flex items-center gap-3 bg-[#0f2040]/90 border border-white/10 px-5 py-2.5 rounded-full">
                  <i className="ri-youtube-line text-red-400 text-lg"></i>
                  <span className="text-white text-sm font-semibold">Watch Full Demo · 3 min</span>
                  <i className="ri-arrow-right-line text-gray-400 text-sm"></i>
                </div>
              </div>
            </div>
          ) : (
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/yjlDLOeLB8M?autoplay=1&rel=0&modestbranding=1"
              title="PRODIFY Demo Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          )}
        </div>

        {/* Below video trust badges */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {[
            { icon: 'ri-shield-check-line', label: 'SARS VAT Compliant', color: 'text-green-400' },
            { icon: 'ri-building-2-line', label: 'Multi-Branch Ready', color: 'text-[#3b82f6]' },
            { icon: 'ri-headphone-line', label: 'SA-Based Support', color: 'text-[#f97316]' },
            { icon: 'ri-lock-line', label: 'POPIA Compliant', color: 'text-yellow-400' },
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-3 bg-[#0f2040] border border-white/10 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className={`${b.icon} ${b.color} text-xl`}></i>
              </div>
              <span className="text-gray-300 text-xs font-medium">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DemoVideo;
